(() => {
  if (window.BafdeSections) return;

  const libraries = new Map();
  const sources = {
    swiper: [document.currentScript?.dataset.bafdeSwiper, 'Swiper'],
    parallax: ['https://cdn.jsdelivr.net/npm/simple-parallax-js@5.5.1/dist/simpleParallax.min.js', 'simpleParallax'],
  };

  function load(name) {
    if (libraries.has(name)) return libraries.get(name);
    const [url, exportName] = sources[name];
    const pending = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = url;
      script.async = true;
      script.onload = () => {
        if (typeof window[exportName] === 'function') resolve(window[exportName]);
        else reject(new Error(`Missing ${exportName} after loading ${url}`));
      };
      script.onerror = () => reject(new Error(`Unable to load ${url}`));
      document.head.appendChild(script);
    });
    libraries.set(name, pending);
    return pending;
  }

  function mount(sectionId, selector, library, initialize, options = {}) {
    const root = document.getElementById(`shopify-section-${sectionId}`);
    if (!root) return;
    const element = root.querySelector(selector);
    if (!element || element.dataset.bafdeMount) return;
    element.dataset.bafdeMount = 'pending';
    const controller = new AbortController();
    let instance;
    let observer;
    let started = false;

    const start = () => {
      if (started || controller.signal.aborted || !root.isConnected) return;
      started = true;
      observer?.disconnect();
      element.removeEventListener('focusin', start);
      element.removeEventListener('pointerdown', start);
      return load(library).then(async (Constructor) => {
        if (options.nearViewport) {
          // Shared library callbacks otherwise initialize every waiting carousel
          // in one microtask batch. Let the browser paint and handle input between them.
          if (window.scheduler?.yield) await window.scheduler.yield();
          else await new Promise((resolve) => setTimeout(resolve, 0));
        }
        if (controller.signal.aborted || !root.isConnected) return;
        instance = initialize(Constructor, element);
        element.dataset.bafdeMount = 'ready';
      }).catch((error) => {
        delete element.dataset.bafdeMount;
        console.error('BAFDE section initialization failed:', sectionId, error);
      });
    };

    const schedule = () => {
      if (controller.signal.aborted || !root.isConnected) return;
      if (!options.nearViewport || window.Shopify?.designMode || !window.IntersectionObserver) {
        start();
        return;
      }
      // Prepare lower product carousels before they enter view, instead of
      // competing with translation, search and the visible hero at startup.
      observer = new window.IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) start();
      }, { rootMargin: '600px 0px' });
      observer.observe(element);
      element.addEventListener('focusin', start, { once: true, signal: controller.signal });
      element.addEventListener('pointerdown', start, { once: true, passive: true, signal: controller.signal });
    };

    if (options.nearViewport && document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', schedule, {once: true, signal: controller.signal});
    } else {
      schedule();
    }

    if (window.Shopify?.designMode) {
      document.addEventListener('shopify:section:unload', (event) => {
        if (event.target !== root) return;
        controller.abort();
        observer?.disconnect();
        instance?.destroy?.(true, true);
        delete element.dataset.bafdeMount;
      }, {signal: controller.signal});
    }
  }

  window.BafdeSections = {mount};
  document.dispatchEvent(new Event('bafde:sections-ready'));
})();
